import "@angular/common/locales/global/fr";
import "@angular/common/locales/global/nl";
import { DATE_PIPE_DEFAULT_OPTIONS } from "@angular/common";
import { Component, inject, NgModule } from "@angular/core";
import { EffectsModule } from "@ngrx/effects";
import { ActionReducer, StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { IonicModule } from "@ionic/angular";
import { provideIonicAngular } from "@ionic/angular/standalone";
import * as Sentry from "@sentry/angular";
import { IcEnvironment } from "@intocare-medapp/environment";

import { WindowRef } from "../tokens";
import { AppModule } from "./app/app.module";
import { IcEvents } from "./ic-events/ic-events.service";
import { monitorStore, reducerProvider, reducerToken, resetStore } from "./shared/index";
import { FiltersEffects } from "./shared/state/effects/filters.effects";
import { AnnouncementsEffects } from "./shared/state/effects/announcements.effects";
import { MedicationTasksEffects } from "./shared/state/effects/medication-tasks.effects";

// Ensure locale data is bundled for correct locale display values, including moment.
// TODO: Improve including locale data, so it's also correctly bundled in unit tests.
//		Preferably, we use the browser APIs so we don't have to include any locale data.
//		See https://intocare.atlassian.net/browse/ICP-18319.
import "@angular/common/locales/global/nl-BE";
import "@angular/common/locales/global/fr-BE";
// TODO: Work away the need for moment locales by using Angular's date pipe or Intl APIs.
//		We could use some things from [ngxtension](https://ngxtension.dev/utilities/intl/intl/).
//		See https://intocare.atlassian.net/browse/ICP-18314.
import "moment";
import "moment/locale/nl-be";
import "moment/locale/fr";

@Component({
	selector: "application-wrapper",
	template: "<sd-app />",
	standalone: false,
})
export class ApplicationWrapperComponent {}

export function windowFactory() {
	const environment = new IcEnvironment().getEnvironment();
	return {
		...window,
		location: {
			...window.location,
			reload: () => {
				if ((window as any).cordova) {
					(window as any).navigator.splashscreen.show();
					(window as any).location = environment.CORDOVA.CDV_FULL_URL;
				} else {
					location.reload();
				}
			},
		},
	};
}

/**
 * First meta reducer in the chain so we can catch all reducer that follow
 */
export function catchAll(reducer: ActionReducer<any>): ActionReducer<any> {
	const icEvents: IcEvents = inject(IcEvents);

	return (state: any, action: any) => {
		try {
			return reducer(state, action);
		} catch (error) {
			Sentry.withScope((scope) => {
				scope.setLevel("fatal");
				Sentry.captureException(error);
			});
			icEvents.notifyFatalReducerError();
			// Rethrow error to avoid ngrx store from further processing the action
			throw error;
		}
	};
}

@NgModule({
	imports: [
		IonicModule.forRoot({ mode: "md", innerHTMLTemplatesEnabled: true }),
		StoreModule.forRoot(reducerToken, {
			runtimeChecks: {
				strictStateImmutability: false,
				strictActionImmutability: false,
			},
			metaReducers: [catchAll, resetStore, monitorStore],
		}),
		EffectsModule.forRoot([FiltersEffects, AnnouncementsEffects, MedicationTasksEffects]),
		// This has serious perfomance impact when enabled
		StoreDevtoolsModule.instrument({ connectInZone: true }),
		AppModule,
	],
	providers: [
		reducerProvider,
		{ provide: WindowRef, useFactory: windowFactory },
		provideIonicAngular({ mode: "md", innerHTMLTemplatesEnabled: true }),
		{ provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: "dd/MM/yy" } },
	],
	declarations: [ApplicationWrapperComponent],
	bootstrap: [ApplicationWrapperComponent],
})
export class MainModule {}
